import React, { useEffect, useState } from "react";
import { MdAccessTime } from "react-icons/md";
import { RxReset } from "react-icons/rx";

import Button from "../../../components/Button";
import { useToaster } from "../../../components/toaster";
import { RokaApiError } from "../../../errors/RokaApiError";
import { useAdminApi } from "../../../hooks/api-hooks/useAdminApi";

interface SiteResetTimeFieldProps {
  siteName: string;
}

interface ResetTimeState {
  currentDay: string;
  default: string;
  isDirty: boolean;
  isLoading: boolean;
}

const SiteResetTimeField = ({ siteName }: SiteResetTimeFieldProps) => {
  const { fetchSiteResetTimes, updateSiteResetTime } = useAdminApi();
  const { showToaster } = useToaster();

  const [state, setState] = useState<ResetTimeState>({
    currentDay: "04:00",
    default: "04:00",
    isDirty: false,
    isLoading: true,
  });

  const [savedValues, setSavedValues] = useState({
    currentDay: "04:00",
    default: "04:00",
  });

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, isLoading: true }));
    fetchSiteResetTimes(siteName)
      .then((values) => {
        if (cancelled) return;
        setState({ ...values, isDirty: false, isLoading: false });
        setSavedValues(values);
      })
      .catch(() => {
        if (cancelled) return;
        setState((prev) => ({ ...prev, isLoading: false }));
      });
    return () => {
      cancelled = true;
    };
  }, [siteName, fetchSiteResetTimes]);

  const updateField = (field: "currentDay" | "default", value: string) => {
    setState((prev) => ({ ...prev, [field]: value, isDirty: true }));
  };

  const handleReset = () => {
    setState((prev) => ({ ...prev, ...savedValues, isDirty: false }));
  };

  const handleSave = async () => {
    try {
      await Promise.all([
        updateSiteResetTime(siteName, state.currentDay, false),
        updateSiteResetTime(siteName, state.default, true),
      ]);
      setSavedValues({ currentDay: state.currentDay, default: state.default });
      setState((prev) => ({ ...prev, isDirty: false }));
      showToaster(`Reset tid for "${siteName}" ble oppdatert!`, {
        type: "success",
      });
    } catch (error) {
      if (error instanceof RokaApiError) {
        showToaster(`Kunne ikke oppdatere reset tid: ${error.message}`, {
          type: "error",
          errorId: error.requestId,
        });
      } else {
        showToaster("En uventet feil oppstod under oppdatering av reset tid", {
          type: "error",
        });
        console.error("Error updating reset time:", error);
      }
    }
  };

  return (
    <div className="form site-form">
      <h4>
        <span className="icon">
          <MdAccessTime />
        </span>
        Reset tid
      </h4>
      <p>Her kan du endre reset tidspunkt for siten.</p>
      <div className="form-group">
        <label>Reset tid i dag</label>
        <input
          type="time"
          value={state.currentDay}
          disabled={state.isLoading}
          onChange={(e) => updateField("currentDay", e.target.value)}
        />
      </div>
      <div className="form-group">
        <label>Standard reset tid</label>
        <input
          type="time"
          value={state.default}
          disabled={state.isLoading}
          onChange={(e) => updateField("default", e.target.value)}
        />
      </div>
      <div className="actions">
        <Button
          disabled={!state.isDirty}
          className="reset-button"
          buttonStyle="outline"
          onClick={handleReset}
        >
          <RxReset />
        </Button>
        <Button
          className="save-button"
          disabled={!state.isDirty}
          onClick={handleSave}
        >
          Lagre endringer
        </Button>
      </div>
    </div>
  );
};

export default SiteResetTimeField;
